import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  loginUser,
  registerUser,
  googleAuth,
  clearAuthError,
  clearAuthFlash,
} from "../store/authSlice";
import { Eye, EyeOff, CheckCircle2, Loader2, ArrowRight, Mail } from "lucide-react";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  ""; // Set in .env — empty string disables the button

/**
 * AuthPage — Unified "Get Started" page.
 *
 * Flow:
 *   1. Google OAuth button (primary, highest visual weight)
 *   2. Divider "or"
 *   3. Email input → "Continue" button
 *   4. If email exists → show password field (login)
 *   5. If email is new → show name + password fields (register)
 *
 * The system determines login vs registration — the user never has to choose.
 */
export default function AuthPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { loading, error, isAuthenticated, justLoggedIn, justRegistered } =
    useSelector((s) => s.auth);

  // Steps: "entry" → "login" | "register"
  const [step, setStep] = useState("entry");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [checking, setChecking] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);

  const redirectTo = searchParams.get("redirect") || "/account";

  // Redirect after auth
  useEffect(() => {
    if (isAuthenticated && (justLoggedIn || justRegistered)) {
      const t = setTimeout(() => {
        dispatch(clearAuthFlash());
        navigate(redirectTo, { replace: true });
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [isAuthenticated, justLoggedIn, justRegistered, navigate, dispatch, redirectTo]);

  useEffect(() => () => dispatch(clearAuthError()), [dispatch]);

  // ── Google Identity Services (GIS) ──────────────────────────────────
  const handleGoogleResponse = useCallback(
    (response) => {
      if (response.credential) {
        dispatch(googleAuth({ credential: response.credential }));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    // Load the GIS script if not already present
    if (!document.getElementById("google-gis-script")) {
      const script = document.createElement("script");
      script.id = "google-gis-script";
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => setGoogleLoaded(true);
      document.head.appendChild(script);
    } else if (window.google?.accounts?.id) {
      setGoogleLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!googleLoaded || !GOOGLE_CLIENT_ID || !window.google?.accounts?.id)
      return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });
  }, [googleLoaded, handleGoogleResponse]);

  const handleGoogleClick = () => {
    if (!window.google?.accounts?.id) return;
    window.google.accounts.id.prompt((notification) => {
      // If One Tap is dismissed or not available, fall through silently
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // Render the button as fallback
        const container = document.getElementById("google-btn-container");
        if (container) {
          container.innerHTML = "";
          window.google.accounts.id.renderButton(container, {
            type: "standard",
            theme: "outline",
            size: "large",
            width: "100%",
            text: "continue_with",
          });
        }
      }
    });
  };

  // ── Email continue ──────────────────────────────────────────────────
  const USER_API =
    process.env.REACT_APP_USER_SERVICE_URL ||
    "https://dgb-vend-backend-o7uw2.ondigitalocean.app/u";

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setChecking(true);
    dispatch(clearAuthError());

    try {
      // Check if email exists
      const res = await fetch(
        `${USER_API}/api/auth/check-email?email=${encodeURIComponent(email.trim())}`,
        { method: "GET" }
      );
      const data = await res.json();
      setStep(data.exists ? "login" : "register");
    } catch {
      // If the check endpoint doesn't exist yet, default to login
      setStep("login");
    } finally {
      setChecking(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    dispatch(loginUser({ email: email.trim(), password }));
  };

  const handleRegister = (e) => {
    e.preventDefault();
    dispatch(
      registerUser({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        password,
      })
    );
  };

  // ── UI ──────────────────────────────────────────────────────────────

  const inputCls =
    "w-full h-12 px-4 rounded-xl font-body text-sm focus:outline-none focus:ring-2 transition-shadow";
  const inputStyle = {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "hsl(var(--foreground))",
    "--tw-ring-color": "var(--vb-blue)",
  };

  const isSuccess = justLoggedIn || justRegistered;

  return (
    <>
      <Navbar />
      <main
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          paddingTop: "calc(var(--header-h, 80px) + 40px)",
          paddingBottom: 60,
        }}
      >
        <div className="w-full max-w-md">
          {/* Success flash */}
          {isSuccess && (
            <div
              className="mb-6 flex items-center gap-3 px-5 py-4 rounded-2xl border"
              style={{
                background: "rgba(91,140,255,0.08)",
                borderColor: "var(--vb-blue)",
              }}
            >
              <CheckCircle2
                className="h-5 w-5 flex-shrink-0"
                style={{ color: "var(--vb-blue)" }}
              />
              <div>
                <p
                  className="font-body font-semibold text-sm"
                  style={{ color: "var(--vb-blue)" }}
                >
                  {justRegistered ? "Account created!" : "Welcome back!"}
                </p>
                <p className="font-body text-xs text-muted-foreground mt-0.5">
                  Redirecting...
                </p>
              </div>
            </div>
          )}

          {/* Card */}
          <div
            className="rounded-3xl border bg-card p-8 md:p-10"
            style={{
              borderColor: "rgba(255,255,255,0.08)",
              boxShadow: "var(--glow-card)",
            }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <h1
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 700,
                  fontSize: 28,
                  color: "hsl(var(--foreground))",
                  marginBottom: 6,
                }}
              >
                Get Started
              </h1>
              <p className="font-body text-sm text-muted-foreground">
                {step === "entry" && "Sign in or create your account in seconds"}
                {step === "login" && "Welcome back — enter your password"}
                {step === "register" && "Just a few details to create your account"}
              </p>
            </div>

            {/* Error */}
            {error && (
              <div
                className="mb-5 px-4 py-3 rounded-xl text-sm font-body"
                style={{
                  background: "rgba(239,68,68,0.08)",
                  color: "#f87171",
                  border: "1px solid rgba(239,68,68,0.2)",
                }}
              >
                {error}
              </div>
            )}

            {/* ── STEP: Entry ── */}
            {step === "entry" && (
              <div className="flex flex-col gap-5">
                {/* Google OAuth */}
                {GOOGLE_CLIENT_ID ? (
                  <>
                    <button
                      onClick={handleGoogleClick}
                      disabled={loading}
                      style={{
                        height: 50,
                        width: "100%",
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.15)",
                        background: "rgba(255,255,255,0.06)",
                        color: "hsl(var(--foreground))",
                        fontFamily: "'Inter', 'Satoshi', sans-serif",
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        transition: "background 0.15s, border-color 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                        e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                        e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                      }}
                    >
                      {/* Google "G" icon */}
                      <svg width="18" height="18" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Continue with Google
                    </button>
                    {/* Fallback container if One Tap is unavailable */}
                    <div id="google-btn-container" />

                    {/* Divider */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: 1,
                          background: "rgba(255,255,255,0.08)",
                        }}
                      />
                      <span
                        className="font-body text-xs text-muted-foreground"
                        style={{ flexShrink: 0 }}
                      >
                        or
                      </span>
                      <div
                        style={{
                          flex: 1,
                          height: 1,
                          background: "rgba(255,255,255,0.08)",
                        }}
                      />
                    </div>
                  </>
                ) : null}

                {/* Email entry */}
                <form onSubmit={handleEmailContinue} className="flex flex-col gap-4">
                  <div>
                    <label className="font-body text-xs font-medium text-muted-foreground mb-1.5 block">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className={inputCls}
                      style={inputStyle}
                      autoFocus
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={checking || loading}
                    className="btn-primary w-full justify-center"
                    style={{ height: 50, borderRadius: 12, fontSize: 15 }}
                  >
                    {checking ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        Continue with Email
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* ── STEP: Login ── */}
            {step === "login" && (
              <form onSubmit={handleLogin} className="flex flex-col gap-4">
                {/* Email display */}
                <div
                  className="flex items-center justify-between px-4 py-3 rounded-xl"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span className="font-body text-sm text-foreground truncate">
                    {email}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("entry");
                      setPassword("");
                      dispatch(clearAuthError());
                    }}
                    className="font-body text-xs font-medium flex-shrink-0 ml-3"
                    style={{
                      color: "var(--vb-blue)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Change
                  </button>
                </div>

                <div>
                  <label className="font-body text-xs font-medium text-muted-foreground mb-1.5 block">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className={`${inputCls} pr-11`}
                      style={inputStyle}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      {showPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center"
                  style={{ height: 50, borderRadius: 12, fontSize: 15 }}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                <div className="text-center mt-1">
                  <Link
                    to="/forgot-password"
                    className="font-body text-xs font-medium"
                    style={{
                      color: "var(--vb-blue)",
                      textDecoration: "none",
                    }}
                  >
                    Forgot your password?
                  </Link>
                </div>
              </form>
            )}

            {/* ── STEP: Register ── */}
            {step === "register" && (
              <form onSubmit={handleRegister} className="flex flex-col gap-4">
                {/* Email display */}
                <div
                  className="flex items-center justify-between px-4 py-3 rounded-xl"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span className="font-body text-sm text-foreground truncate">
                    {email}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("entry");
                      setPassword("");
                      setFirstName("");
                      setLastName("");
                      dispatch(clearAuthError());
                    }}
                    className="font-body text-xs font-medium flex-shrink-0 ml-3"
                    style={{
                      color: "var(--vb-blue)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Change
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-body text-xs font-medium text-muted-foreground mb-1.5 block">
                      First Name
                    </label>
                    <input
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Jane"
                      className={inputCls}
                      style={inputStyle}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs font-medium text-muted-foreground mb-1.5 block">
                      Last Name
                    </label>
                    <input
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className={inputCls}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div>
                  <label className="font-body text-xs font-medium text-muted-foreground mb-1.5 block">
                    Create Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 10 chars, 1 letter, 1 number, 1 symbol"
                      className={`${inputCls} pr-11`}
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      {showPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center"
                  style={{ height: 50, borderRadius: 12, fontSize: 15 }}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Trust signals */}
            <div
              className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-6 pt-5"
              style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
            >
              {["Secure login", "No spam", "Used by 500+ operators"].map(
                (t) => (
                  <span
                    key={t}
                    className="font-body text-[11px] text-muted-foreground flex items-center gap-1.5"
                  >
                    <span style={{ color: "var(--vb-blue)", fontSize: 10 }}>
                      ✔
                    </span>
                    {t}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
